import {NormalizedReport, ReportDetail, ReportGroup, ReportRule} from './normalized-report.model';

/**
 * Ein Ergebnis-Detail, das konkrete Bauteile benennt (IFC-GUIDs).
 *
 * Die GUID-Listen stammen aus dem Roh-Output der Prüfmodule und hängen NICHT am Prüftyp:
 * Der IDS-Check legt sie je Requirement ab (`IdsReportAdapter`), der BIM-Basis-Check je
 * Spezifikation (`specBasedSpecDetail`) – und Letzterer läuft sowohl als eigenständiger Bericht
 * (`checkType === 'BIM_BASIS'`) als auch als Prüfregel innerhalb eines Hybrid-Regelsets, wo der
 * Bericht als `HYBRID` firmiert. Deshalb wird hier über die STRUKTUR erkannt, nicht über das
 * Typ-Label.
 */
export interface ReportGuidFinding {
  group: ReportGroup;
  rule: ReportRule;
  detail: ReportDetail;
  /** GUIDs der Bauteile, die die Anforderung NICHT erfüllen. */
  guidListFail: string[];
  /** GUIDs der Bauteile, die die Anforderung erfüllen. */
  guidListCorrect: string[];
}

/** GUID-Liste aus einem Roh-Output lesen; alles Nicht-String wird verworfen. */
function guidList(rawOutput: any, key: 'guidListFail' | 'guidListCorrect'): string[] {
  const values = rawOutput?.[key];
  if (!Array.isArray(values)) return [];
  return values
    .map(value => typeof value === 'string' ? value.trim() : '')
    .filter(value => value.length > 0);
}

/** Alle Details eines Berichts, die Bauteil-GUIDs mitführen – in Berichtsreihenfolge. */
export function collectGuidFindings(report: NormalizedReport | null | undefined): ReportGuidFinding[] {
  if (!report) return [];

  const findings: ReportGuidFinding[] = [];
  for (const group of report.groups) {
    for (const rule of group.rules) {
      for (const detail of rule.details) {
        const guidListFail = guidList(detail.rawOutput, 'guidListFail');
        const guidListCorrect = guidList(detail.rawOutput, 'guidListCorrect');
        if (guidListFail.length === 0 && guidListCorrect.length === 0) continue;
        findings.push({group, rule, detail, guidListFail, guidListCorrect});
      }
    }
  }
  return findings;
}

/**
 * Enthält der Bericht Ergebnisse, die sich auf konkrete Bauteile beziehen lassen?
 *
 * Kriterium für den „Öffnen"-Button: ohne GUIDs gibt es im Viewer nichts hervorzuheben.
 * Bewusst auch bei ausschließlich erfüllten Anforderungen `true` – das Modell zu öffnen ist dann
 * weiterhin sinnvoll, es entstehen nur keine BCF-Topics.
 */
export function reportHasElementGuids(report: NormalizedReport | null | undefined): boolean {
  return collectGuidFindings(report).length > 0;
}
